import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, Search } from "lucide-react";
import type { ResultJobStatusResponse, ResultJobSubmitResponse, ResultLookupResponse, TokenResponse } from "../shared/types";
import { DirectorateField } from "./components/DirectorateField";
import { SearchableSelect } from "./components/SearchableSelect";
import { Turnstile } from "./components/Turnstile";
import { ResultView } from "./components/ResultView";
import { useCatalog } from "./hooks/useCatalog";
import { apiClient } from "./services/apiClient";
import { toFriendlyError } from "./services/errorMessages";

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const LAST_SEARCH_KEY = "moed-results:last-search";
const LAST_JOB_ID_KEY = "moed-results:last-job-id";
const DEFAULT_DIRECTORATE_ID = 8869;
const DEFAULT_CERTIFICATE_ID = 281;
const TURNSTILE_BYPASS_PATH = "/darklightisthebest";

type StoredSearch = {
  subscriptionNumber?: string;
  directorateId?: number;
  certificateId?: number;
};

const ui = {
  title: "\u0646\u062a\u0627\u0626\u062c \u0627\u0644\u0627\u0645\u062a\u062d\u0627\u0646\u0627\u062a",
  intro:
    "\u0627\u0643\u062a\u0628 \u0631\u0642\u0645 \u0627\u0644\u0627\u0643\u062a\u062a\u0627\u0628 \u0648\u0627\u062e\u062a\u0631 \u0627\u0644\u0645\u062d\u0627\u0641\u0638\u0629 \u0648\u0627\u0644\u0634\u0647\u0627\u062f\u0629. \u0633\u0646\u0639\u0631\u0636 \u0627\u0644\u0646\u062a\u064a\u062c\u0629 \u0647\u0646\u0627 \u0645\u0628\u0627\u0634\u0631\u0629 \u0639\u0646\u062f \u062a\u0648\u0641\u0631\u0647\u0627.",
  step: "\u0661",
  enterData: "\u0623\u062f\u062e\u0644 \u0627\u0644\u0628\u064a\u0627\u0646\u0627\u062a",
  help: "\u062a\u0623\u0643\u062f \u0645\u0646 \u0627\u0644\u0631\u0642\u0645 \u0648\u0627\u0644\u0627\u062e\u062a\u064a\u0627\u0631\u0627\u062a \u0642\u0628\u0644 \u0627\u0644\u0636\u063a\u0637 \u0639\u0644\u0649 \u0632\u0631 \u0627\u0644\u0628\u062d\u062b.",
  subscriptionNumber: "\u0631\u0642\u0645 \u0627\u0644\u0627\u0643\u062a\u062a\u0627\u0628",
  example: "\u0645\u062b\u0627\u0644: 1089173",
  certificate: "\u0627\u0644\u0634\u0647\u0627\u062f\u0629",
  chooseCertificate: "\u0627\u062e\u062a\u0631 \u0627\u0644\u0634\u0647\u0627\u062f\u0629",
  searchCertificate: "\u0627\u0628\u062d\u062b \u0639\u0646 \u0627\u0644\u0634\u0647\u0627\u062f\u0629",
  noCertificate: "\u0644\u0627 \u062a\u0648\u062c\u062f \u0634\u0647\u0627\u062f\u0629 \u0628\u0647\u0630\u0627 \u0627\u0644\u0627\u0633\u0645.",
  searching: "\u062c\u0627\u0631\u064a \u0627\u0644\u0628\u062d\u062b",
  queuedTitle: "\u0637\u0644\u0628\u0643 \u0641\u064a \u0642\u0627\u0626\u0645\u0629 \u0627\u0644\u0627\u0646\u062a\u0638\u0627\u0631",
  queuePosition: "\u062a\u0631\u062a\u064a\u0628\u0643 \u0641\u064a \u0627\u0644\u0642\u0627\u0626\u0645\u0629",
  showResult: "\u0625\u0638\u0647\u0627\u0631 \u0627\u0644\u0646\u062a\u064a\u062c\u0629",
  completeVerification: "\u0623\u0643\u0645\u0644 \u0627\u0644\u062a\u062d\u0642\u0642 \u0623\u0648\u0644\u0627",
  verificationLoading: "\u062c\u0627\u0631\u064a \u062a\u062b\u0628\u064a\u062a \u0627\u0644\u062a\u062d\u0642\u0642",
  verificationRequired: "\u0623\u0643\u0645\u0644 \u0645\u0631\u0628\u0639 \u0627\u0644\u062a\u062d\u0642\u0642 \u062b\u0645 \u062d\u0627\u0648\u0644 \u0645\u0631\u0629 \u0623\u062e\u0631\u0649.",
  turnstileDomainError: "\u062e\u062f\u0645\u0629 \u0627\u0644\u062a\u062d\u0642\u0642 \u063a\u064a\u0631 \u0645\u0641\u0639\u0644\u0629 \u0644\u0647\u0630\u0627 \u0627\u0644\u0631\u0627\u0628\u0637. \u0627\u0641\u062a\u062d \u0627\u0644\u0645\u0648\u0642\u0639 \u0645\u0646 \u0627\u0644\u0631\u0627\u0628\u0637 \u0627\u0644\u0631\u0633\u0645\u064a \u0623\u0648 \u062d\u0627\u0648\u0644 \u0644\u0627\u062d\u0642\u0627.",
  turnstileGeneralError: "\u062a\u0639\u0630\u0631 \u062a\u0634\u063a\u064a\u0644 \u0627\u0644\u062a\u062d\u0642\u0642. \u062d\u062f\u062b \u0627\u0644\u0635\u0641\u062d\u0629 \u0623\u0648 \u062c\u0631\u0628 \u0645\u062a\u0635\u0641\u062d\u0627 \u0622\u062e\u0631.",
  searchingTitle: "\u062c\u0627\u0631\u064a \u0627\u0644\u0628\u062d\u062b \u0639\u0646 \u0627\u0644\u0646\u062a\u064a\u062c\u0629",
  searchingHelp: "\u0642\u062f \u064a\u0633\u062a\u063a\u0631\u0642 \u0627\u0644\u0623\u0645\u0631 \u0642\u0644\u064a\u0644\u0627. \u0644\u0627 \u062a\u063a\u0644\u0642 \u0627\u0644\u0635\u0641\u062d\u0629.",
  ready: "\u062c\u0627\u0647\u0632 \u0644\u0644\u0628\u062d\u062b",
  readyHelp:
    "\u0628\u0639\u062f \u0625\u062f\u062e\u0627\u0644 \u0627\u0644\u0628\u064a\u0627\u0646\u0627\u062a \u0648\u0627\u0644\u0636\u063a\u0637 \u0639\u0644\u0649 \u0632\u0631 \u0625\u0638\u0647\u0627\u0631 \u0627\u0644\u0646\u062a\u064a\u062c\u0629\u060c \u0633\u062a\u0638\u0647\u0631 \u0627\u0644\u062a\u0641\u0627\u0635\u064a\u0644 \u0647\u0646\u0627.",
  footer:
    "\u0647\u0630\u0627 \u0627\u0644\u0645\u0648\u0642\u0639 \u0639\u0645\u0644 \u062a\u0637\u0648\u0639\u064a \u063a\u064a\u0631 \u0631\u0628\u062d\u064a\u060c \u0648\u0644\u0627 \u064a\u062a\u0628\u0639 \u0644\u0623\u064a \u062c\u0647\u0629 \u0631\u0633\u0645\u064a\u0629. \u064a\u0639\u0631\u0636 \u0627\u0644\u0646\u062a\u0627\u0626\u062c \u0643\u0645\u0627 \u062a\u0635\u0644 \u0645\u0646 \u0627\u0644\u0645\u0635\u062f\u0631 \u062f\u0648\u0646 \u062a\u0639\u062f\u064a\u0644.",
};

const readLastSearch = (): StoredSearch => {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(LAST_SEARCH_KEY);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as StoredSearch;
  } catch {
    return {};
  }
};

const saveLastSearch = (search: Required<StoredSearch>): void => {
  try {
    window.localStorage.setItem(LAST_SEARCH_KEY, JSON.stringify(search));
  } catch {
    // localStorage may be disabled; search should still work.
  }
};

const readLastJobStatus = (): ResultJobStatusResponse | null => {
  try {
    const raw = window.localStorage.getItem(LAST_JOB_ID_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as ResultJobStatusResponse | string;
    if (typeof parsed === "string") {
      return createPendingJobStatus(parsed);
    }
    return parsed?.jobId ? parsed : null;
  } catch {
    return null;
  }
};

const saveLastJobStatus = (status: ResultJobStatusResponse): void => {
  try {
    window.localStorage.setItem(LAST_JOB_ID_KEY, JSON.stringify(status));
  } catch {
    // localStorage may be disabled; polling should still work in this tab.
  }
};

const clearLastJobId = (): void => {
  try {
    window.localStorage.removeItem(LAST_JOB_ID_KEY);
  } catch {
    // localStorage may be disabled; nothing to clear.
  }
};

const createPendingJobStatus = (jobId: string, queuePosition?: number): ResultJobStatusResponse => ({
  jobId,
  status: "pending",
  ...(queuePosition !== undefined ? { queuePosition } : {}),
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const saveAndReturnJobStatus = (status: ResultJobStatusResponse): ResultJobStatusResponse => {
  saveLastJobStatus(status);
  return status;
};

export const App = () => {
  const [storedSearch] = useState<StoredSearch>(() => readLastSearch());
  const { catalog, error: catalogError, loading: catalogLoading } = useCatalog();
  const [subscriptionNumber, setSubscriptionNumber] = useState(storedSearch.subscriptionNumber ?? "");
  const [directorateId, setDirectorateId] = useState<number | undefined>(storedSearch.directorateId);
  const [certificateId, setCertificateId] = useState<number | undefined>(storedSearch.certificateId);
  const [turnstileSession, setTurnstileSession] = useState<TokenResponse | null>(null);
  const [turnstileExchanging, setTurnstileExchanging] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);
  const [result, setResult] = useState<ResultLookupResponse | null>(null);
  const [jobStatus, setJobStatus] = useState<ResultJobStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const notifiedJobIds = useRef<Set<string>>(new Set());

  const selectedDirectorateId = directorateId ?? DEFAULT_DIRECTORATE_ID;
  const selectedCertificateId = certificateId ?? DEFAULT_CERTIFICATE_ID;
  const turnstileBypassed = typeof window !== "undefined" && window.location.pathname === TURNSTILE_BYPASS_PATH;
  const turnstileRequired = Boolean(turnstileSiteKey) && !turnstileBypassed;
  const turnstileSessionToken =
    turnstileSession && Date.parse(turnstileSession.expiresAt) > Date.now() ? turnstileSession.token : undefined;
  const jobInProgress = jobStatus?.status === "pending" || jobStatus?.status === "processing";
  const canSubmit =
    !loading &&
    !jobInProgress &&
    !catalogLoading &&
    !turnstileExchanging &&
    Boolean(subscriptionNumber.trim()) &&
    (!turnstileRequired || Boolean(turnstileSessionToken));

  const handleTurnstileToken = useCallback(async (token: string | undefined) => {
    if (!token) {
      setTurnstileSession((current) => (current && Date.parse(current.expiresAt) > Date.now() ? current : null));
      return;
    }

    setTurnstileExchanging(true);
    try {
      const session = await apiClient.createTurnstileSession({ turnstileToken: token });
      setTurnstileSession(session);
    } catch (err) {
      setTurnstileSession(null);
      setError(toFriendlyError(err));
    } finally {
      setTurnstileExchanging(false);
    }
  }, []);

  const handleTurnstileError = useCallback((errorCode: string) => {
    setError(errorCode === "110200" ? ui.turnstileDomainError : ui.turnstileGeneralError);
  }, []);

  useEffect(() => {
    if (!turnstileSession) {
      return undefined;
    }

    const expiresAtMs = Date.parse(turnstileSession.expiresAt);
    const timeoutMs = Math.max(expiresAtMs - Date.now(), 0);
    const timeoutId = window.setTimeout(() => {
      setTurnstileSession(null);
      setResetSignal((value) => value + 1);
    }, timeoutMs);

    return () => window.clearTimeout(timeoutId);
  }, [turnstileSession]);

  const notifyJobComplete = useCallback((jobId: string): void => {
    if (typeof window === "undefined" || !("Notification" in window) || notifiedJobIds.current.has(jobId)) {
      return;
    }
    if (Notification.permission === "granted") {
      notifiedJobIds.current.add(jobId);
      new Notification("\u0638\u0647\u0631\u062a \u0627\u0644\u0646\u062a\u064a\u062c\u0629");
    }
  }, []);

  const applyJobStatus = useCallback(
    (nextStatus: ResultJobStatusResponse): void => {
      setJobStatus(nextStatus);
      if (nextStatus.status === "complete" && nextStatus.result) {
        saveLastJobStatus(nextStatus);
        setResult(nextStatus.result);
        setError(null);
        setLoading(false);
        notifyJobComplete(nextStatus.jobId);
        return;
      }

      if (nextStatus.status === "failed") {
        saveLastJobStatus(nextStatus);
        setResult(null);
        setError(nextStatus.error?.message ?? "\u0644\u0645 \u0646\u062a\u0645\u0643\u0646 \u0645\u0646 \u062c\u0644\u0628 \u0627\u0644\u0646\u062a\u064a\u062c\u0629 \u0627\u0644\u0622\u0646. \u062d\u0627\u0648\u0644 \u0645\u0631\u0629 \u0623\u062e\u0631\u0649.");
        setLoading(false);
        return;
      }

      saveLastJobStatus(nextStatus);
      setError(null);
      setLoading(true);
    },
    [notifyJobComplete],
  );

  useEffect(() => {
    const savedStatus = readLastJobStatus();
    if (!savedStatus) {
      return;
    }

    setJobStatus(savedStatus);
    setResult(savedStatus.result ?? null);
    setError(savedStatus.status === "failed" ? (savedStatus.error?.message ?? null) : null);
    if (savedStatus.status === "complete" || savedStatus.status === "failed") {
      setLoading(false);
      return;
    }

    setLoading(true);
    void apiClient
      .getResultJob(savedStatus.jobId)
      .then(applyJobStatus)
      .catch((err) => {
        setError(toFriendlyError(err));
        setLoading(true);
      });
  }, [applyJobStatus]);

  useEffect(() => {
    if (!jobStatus || jobStatus.status === "complete" || jobStatus.status === "failed") {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        const nextStatus = await apiClient.getResultJob(jobStatus.jobId);
        applyJobStatus(nextStatus);
      } catch (err) {
        setError(toFriendlyError(err));
        setLoading(true);
        setJobStatus((current) =>
          current?.jobId === jobStatus.jobId ? saveAndReturnJobStatus({ ...current, updatedAt: new Date().toISOString() }) : current,
        );
      }
    }, 3_000);

    return () => window.clearTimeout(timeoutId);
  }, [applyJobStatus, jobStatus]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedDirectorateId || !selectedCertificateId || loading) {
      return;
    }
    if (turnstileRequired && !turnstileSessionToken) {
      setError(ui.verificationRequired);
      return;
    }

    const search = {
      subscriptionNumber: subscriptionNumber.trim(),
      directorateId: selectedDirectorateId,
      certificateId: selectedCertificateId,
    };
    saveLastSearch(search);
    setLoading(true);
    setError(null);
    setResult(null);
    setJobStatus(null);

    try {
      const data = await apiClient.lookupResult({ ...search, turnstileToken: turnstileSessionToken });
      if (isResultJobSubmit(data)) {
        const nextStatus = createPendingJobStatus(data.jobId, data.queuePosition);
        saveLastJobStatus(nextStatus);
        setJobStatus(nextStatus);
        requestNotificationPermission();
        return;
      }
      clearLastJobId();
      setResult(data);
      setLoading(false);
    } catch (err) {
      setError(toFriendlyError(err));
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-zinc-950 text-zinc-50" dir="rtl">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-5 sm:px-6 lg:px-8">
        <header className="border-b border-white/10 pb-5">
          <div className="flex flex-col gap-3">
            <div className="h-1 w-20 rounded-sm bg-teal-400" aria-hidden="true" />
            <div>
              <h1 className="mt-1 text-3xl font-bold text-white sm:text-4xl">{ui.title}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-zinc-300">{ui.intro}</p>
            </div>
          </div>
        </header>

        <div className="grid flex-1 gap-5 py-5 lg:grid-cols-[410px_1fr]">
          <section className="h-fit rounded-md border border-white/10 bg-zinc-900/95 p-5 shadow-2xl shadow-black/20 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-teal-500 text-sm font-bold text-zinc-950">
                {ui.step}
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">{ui.enterData}</h2>
                <p className="mt-1 text-sm leading-6 text-zinc-400">{ui.help}</p>
              </div>
            </div>

            <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
              <label className="block">
                <span className="text-sm font-semibold text-zinc-200">{ui.subscriptionNumber}</span>
                <input
                  value={subscriptionNumber}
                  onChange={(event) => setSubscriptionNumber(event.target.value.replace(/\D/g, ""))}
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={32}
                  placeholder={ui.example}
                  className="mt-2 h-12 w-full rounded-sm border border-zinc-700 bg-zinc-950 px-3 text-lg font-semibold text-white outline-none transition placeholder:text-sm placeholder:font-normal placeholder:text-zinc-500 focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20"
                  required
                />
              </label>

              <DirectorateField
                directorates={catalog?.directorates ?? []}
                selectedId={selectedDirectorateId}
                disabled={catalogLoading || !catalog}
                onSelect={setDirectorateId}
              />

              <SearchableSelect
                label={ui.certificate}
                options={catalog?.certificates ?? []}
                selectedId={selectedCertificateId}
                disabled={catalogLoading || !catalog}
                placeholder={ui.chooseCertificate}
                searchPlaceholder={ui.searchCertificate}
                emptyText={ui.noCertificate}
                onSelect={setCertificateId}
              />

              <Turnstile
                siteKey={turnstileRequired && !turnstileSessionToken ? turnstileSiteKey : undefined}
                resetSignal={resetSignal}
                onToken={handleTurnstileToken}
                onError={handleTurnstileError}
              />

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-sm bg-teal-400 px-4 text-base font-bold text-zinc-950 shadow-lg shadow-teal-950/30 transition hover:bg-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:ring-offset-2 focus:ring-offset-zinc-950 disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:text-zinc-400 disabled:shadow-none"
              >
                {loading || turnstileExchanging ? (
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                ) : (
                  <Search className="h-5 w-5" aria-hidden="true" />
                )}
                <span>
                  {loading
                    ? ui.searching
                    : turnstileExchanging
                      ? ui.verificationLoading
                      : turnstileRequired && !turnstileSessionToken
                        ? ui.completeVerification
                        : ui.showResult}
                </span>
              </button>
            </form>

          </section>

          <section className="min-h-[520px]">
            {catalogError ? <Message message={catalogError} /> : null}
            {error ? <Message message={error} /> : null}
            {loading ? <LoadingPanel jobStatus={jobStatus} /> : null}
            {!loading && result ? <ResultView data={result} /> : null}
            {!loading && !result && !error && !catalogError ? <EmptyPanel /> : null}
          </section>
        </div>

        <footer className="border-t border-white/10 py-4 text-center text-xs leading-6 text-zinc-500">
          <span>{ui.footer}</span>
          <a className="ms-2 text-teal-300 underline-offset-4 hover:underline" href="mailto:info@abdulhafizfaraj.dev">
            للتواصل معنا
          </a>
        </footer>
      </div>
    </main>
  );
};

const Message = ({ message }: { message: string }) => (
  <div className="mb-4 flex items-start gap-3 whitespace-pre-line rounded-md border border-red-400/30 bg-red-950/45 p-4 text-sm leading-7 text-red-100">
    <AlertCircle className="mt-1 h-5 w-5 shrink-0" aria-hidden="true" />
    <p>{message}</p>
  </div>
);

const LoadingPanel = ({ jobStatus }: { jobStatus: ResultJobStatusResponse | null }) => (
  <div className="flex min-h-[520px] items-center justify-center rounded-md border border-white/10 bg-zinc-900 p-8 shadow-2xl shadow-black/20">
    <div className="text-center text-zinc-300">
      <Loader2 className="mx-auto h-7 w-7 animate-spin text-teal-400" aria-hidden="true" />
      <p className="mt-3 text-base font-bold">{jobStatus ? ui.queuedTitle : ui.searchingTitle}</p>
      {jobStatus?.queuePosition ? (
        <p className="mt-2 text-sm font-semibold text-teal-200">
          {ui.queuePosition}: {jobStatus.queuePosition}
        </p>
      ) : null}
      <p className="mt-1 text-sm text-zinc-500">{ui.searchingHelp}</p>
    </div>
  </div>
);

const isResultJobSubmit = (
  response: ResultLookupResponse | ResultJobSubmitResponse,
): response is ResultJobSubmitResponse => "jobId" in response;

const requestNotificationPermission = (): void => {
  if (typeof window === "undefined" || !("Notification" in window) || Notification.permission !== "default") {
    return;
  }
  void Notification.requestPermission();
};

const EmptyPanel = () => (
  <div className="flex min-h-[520px] items-center justify-center rounded-md border border-dashed border-zinc-700 bg-zinc-900/80 p-8 text-center">
    <div className="max-w-sm">
      <CheckCircle2 className="mx-auto h-9 w-9 text-teal-400" aria-hidden="true" />
      <p className="mt-3 text-xl font-bold text-white">{ui.ready}</p>
      <p className="mt-2 text-sm leading-7 text-zinc-400">{ui.readyHelp}</p>
    </div>
  </div>
);
